import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card'
import { Mail, Phone, MapPin } from 'lucide-react'

interface ContactInformation {
  email?: string
  phone_number?: string
  alternate_phone_number?: string
  address: {
    street?: string
    apartment?: string
    city?: string
    state?: string
    postal_code?: string
    country?: string
  }
}

interface KycContactInfoProps {
  contactInfo: ContactInformation
}

export const KycContactInfo: React.FC<KycContactInfoProps> = ({ contactInfo }) => {
  const formatAddress = () => {
    const parts = [
      contactInfo.address.street,
      contactInfo.address.apartment,
      contactInfo.address.city,
      contactInfo.address.state,
      contactInfo.address.postal_code,
      contactInfo.address.country
    ].filter(Boolean)
    
    if (parts.length === 0) return 'Not provided'
    return parts.join(', ')
  }

  const hasContactInfo = 
    contactInfo.email || 
    contactInfo.phone_number || 
    contactInfo.alternate_phone_number

  const hasAddress = Object.values(contactInfo.address).some(value => value && value.trim() !== '')

  if (!hasContactInfo && !hasAddress) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center space-x-2 text-base">
            <Mail className="h-4 w-4" />
            <span>Contact Information</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground">No contact information provided</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center space-x-2 text-base">
          <Mail className="h-4 w-4" />
          <span>Contact Information</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Email and Phone */}
          {hasContactInfo && (
            <div className="space-y-2">
              {contactInfo.email && (
                <div className="flex items-start space-x-2">
                  <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Email</label>
                    <p className="text-sm">{contactInfo.email}</p>
                  </div>
                </div>
              )}

              {contactInfo.phone_number && (
                <div className="flex items-start space-x-2">
                  <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Phone Number</label>
                    <p className="text-sm">{contactInfo.phone_number}</p>
                  </div>
                </div>
              )}

              {contactInfo.alternate_phone_number && (
                <div className="flex items-start space-x-2">
                  <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Alternate Phone</label>
                    <p className="text-sm">{contactInfo.alternate_phone_number}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Address */}
          {hasAddress && (
            <div className="flex items-start space-x-2 pt-2">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div className="flex-1">
                <label className="text-xs font-medium text-muted-foreground">Address</label>
                <p className="text-sm">{formatAddress()}</p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
